import { useState, useRef, useCallback } from "react";

// Tracks a single active print job so the whole page shares ONE prominent,
// always-reachable STOP control. Holds an AbortController whose signal is
// threaded into the Zebra print loops and the label-building fetches, so a
// STOP click halts sending immediately (labels already in the printer's own
// buffer must be cleared at the hardware — see PrintStopBanner).
export function usePrintJob() {
  const [job, setJob] = useState(null);   // { label, done, total } | null
  const ref = useRef(null);

  const start = useCallback((label, total = 0) => {
    const ctrl = new AbortController();
    ref.current = ctrl;
    setJob({ label, done: 0, total });
    return ctrl.signal;
  }, []);

  const progress = useCallback((done, total) => {
    setJob((j) => (j ? { ...j, done, ...(total != null ? { total } : {}) } : j));
  }, []);

  const setLabel = useCallback((label) => {
    setJob((j) => (j ? { ...j, label } : j));
  }, []);

  const end = useCallback(() => { ref.current = null; setJob(null); }, []);

  const stop = useCallback(() => { ref.current?.abort(); }, []);

  const aborted = useCallback(() => !!ref.current?.signal.aborted, []);

  return { job, start, progress, setLabel, end, stop, aborted };
}
